/*
 * Featured collections slider.
 * Scoped custom element: no document-wide listeners, no inline handlers.
 * The slider itself is native overflow scrolling with CSS scroll-snap; this element
 * only decides whether the cards already fit (centre them and hide the scrollbar)
 * or whether the section really needs to scroll.
 */
if (!customElements.get('featured-collections-slider')) {
  customElements.define(
    'featured-collections-slider',
    class FeaturedCollectionsSlider extends HTMLElement {
      connectedCallback() {
        this.viewport = this.querySelector('[data-featured-collections-viewport]');
        this.track = this.querySelector('[data-featured-collections-track]');
        if (!this.viewport || !this.track) return;

        this.update = this.update.bind(this);
        this.onScroll = this.onScroll.bind(this);
        this.onPrev = this.scrollByCards.bind(this, -1);
        this.onNext = this.scrollByCards.bind(this, 1);

        this.prevBtn = this.querySelector('[data-featured-collections-prev]');
        this.nextBtn = this.querySelector('[data-featured-collections-next]');
        if (this.prevBtn) this.prevBtn.addEventListener('click', this.onPrev);
        if (this.nextBtn) this.nextBtn.addEventListener('click', this.onNext);
        this.viewport.addEventListener('scroll', this.onScroll, { passive: true });

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(this.update);
          this.resizeObserver.observe(this);
        } else {
          window.addEventListener('resize', this.update);
        }

        this.update();
      }

      disconnectedCallback() {
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
          this.resizeObserver = null;
        } else {
          window.removeEventListener('resize', this.update);
        }
        if (this.prevBtn) this.prevBtn.removeEventListener('click', this.onPrev);
        if (this.nextBtn) this.nextBtn.removeEventListener('click', this.onNext);
        if (this.viewport) this.viewport.removeEventListener('scroll', this.onScroll);
        if (this.scrollFrame) {
          cancelAnimationFrame(this.scrollFrame);
          this.scrollFrame = null;
        }
      }

      scrollByCards(direction) {
        if (!this.viewport || !this.track) return;
        const card = this.track.querySelector('.featured-collections__card');
        const gap = parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap) || 0;
        const step = card ? card.getBoundingClientRect().width + gap : this.viewport.clientWidth * 0.8;
        this.viewport.scrollBy({ left: step * direction, behavior: 'smooth' });
      }

      onScroll() {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.updateNavState();
        });
      }

      updateNavState() {
        if (!this.viewport) return;
        const max = this.viewport.scrollWidth - this.viewport.clientWidth;
        const pos = this.viewport.scrollLeft;
        if (this.prevBtn) this.prevBtn.toggleAttribute('disabled', pos <= 1);
        if (this.nextBtn) this.nextBtn.toggleAttribute('disabled', pos >= max - 1);
      }

      update() {
        if (!this.viewport || !this.track) return;

        // Measure against the un-centred, un-collapsed state so the check is stable.
        this.track.classList.remove('featured-collections__track--centered');
        this.viewport.classList.remove('featured-collections__viewport--fits');

        const fits = this.track.scrollWidth <= this.viewport.clientWidth + 1;
        const isDesktop = window.matchMedia('(min-width: 750px)').matches;

        if (fits && isDesktop) {
          this.track.classList.add('featured-collections__track--centered');
          this.viewport.classList.add('featured-collections__viewport--fits');
          this.viewport.removeAttribute('tabindex');
          this.viewport.removeAttribute('role');
        } else {
          // A scrollable region must be reachable by keyboard.
          this.viewport.setAttribute('tabindex', '0');
          this.viewport.setAttribute('role', 'group');
        }

        // Arrows only make sense when there is somewhere to scroll.
        this.classList.toggle('featured-collections--no-scroll', fits);
        this.updateNavState();
      }
    }
  );
}
